import { type Action, type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import type { GroundhogAnalysisResponse, LanguageEnum } from 'js/src/client';
import {
    addIdeaOutlineReference,
    clearGroundhogAnalysisForText,
    deleteIdeaOutlineReference,
    removeScriptFromCache,
    resetScriptCache,
    setCurrentAIAvatar,
    setCurrentAIAvatarVoicePreset,
    setCurrentAIVoice,
    setDocToScriptData,
    setDocToScriptImportType,
    setDocToScriptOptions,
    setDocToScriptPage,
    setEditedScript,
    setEditedScriptInitialWordCount,
    setEditedScriptLanguage,
    setGroundhogAnalysis,
    setIdeaOutlineReference,
    setIdeaOutlineSubject,
    setIsConvertingToVoiceover,
    setIsFetchingAIScriptOptions,
    setIsFetchingScriptFromIdea,
    setIsFetchingScriptOptionAIScript,
    setIsUploadingScriptImages,
    setLongestSupportedScriptDuration,
    setScriptForScriptOption,
    setScriptSentences,
    setSelectedBlockText,
    setSelectedScriptOption,
    setTargetDuration,
    updateDocToScriptOption,
} from 'js/src/data/script-composer/script-composer-actions';
import type {
    DOC_TO_SCRIPT_PAGE,
    DocToScriptData,
    ImmutableIdeaOutlineValueSet,
    Script,
    ScriptOption,
} from 'js/src/data/script-composer/script-composer-types';
import { DEFAULT_IDEA_OUTLINE_VALUES } from 'js/src/data/script-composer/script-composer-utils';
import type { SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';

export function selectedScriptOption(state: ScriptOption | null = null, action: AnyAction) {
    if (setSelectedScriptOption.match(action)) {
        return action.payload.scriptOption;
    }
    return state;
}

export function docToScriptPage(state: DOC_TO_SCRIPT_PAGE | null = null, action: AnyAction) {
    if (setDocToScriptPage.match(action)) {
        return action.payload.page;
    }
    return state;
}

export function docToScriptData(
    state: DocToScriptData = {
        url: '',
        originalEditorState: undefined,
        shouldSkipAISummarization: undefined,
    },
    action: AnyAction,
) {
    if (setDocToScriptData.match(action)) {
        return Object.assign({}, state, action.payload);
    }
    return state;
}

export function docToScriptImportType(
    state: SCRIPT_CREATION_SOURCES | null = null,
    action: Action,
) {
    if (setDocToScriptImportType.match(action)) {
        return action.payload;
    }
    return state;
}

export function scriptCache(
    state: Immutable.Map<string, Script> = Immutable.Map(),
    action: AnyAction,
) {
    // data structure is a map of scriptOptionId to Scripts
    if (setScriptForScriptOption.match(action)) {
        return state.set(action.payload.scriptOptionId, action.payload.scriptData);
    }

    if (removeScriptFromCache.match(action)) {
        if (state.has(action.payload.scriptOptionId)) {
            state = state.remove(action.payload.scriptOptionId);
        }
        return state;
    }

    if (resetScriptCache.match(action)) {
        state = Immutable.Map();
    }
    return state;
}

export function editedScript(state: Script | null = null, action: AnyAction) {
    if (setEditedScript.match(action)) {
        return action.payload.scriptData;
    }
    return state;
}

export function editedScriptInitialWordCount(state = 0, action: AnyAction) {
    if (setEditedScriptInitialWordCount.match(action)) {
        return action.payload.editedScriptInitialWordCount;
    }
    return state;
}

export function editedScriptLanguage(state: LanguageEnum | null = null, action: AnyAction) {
    if (setEditedScriptLanguage.match(action)) {
        return action.payload;
    }
    return state;
}

export function scriptSentences(state: string[] = [], action: AnyAction) {
    /**
     * NOTE: This is used for storing reference material for Public Blueprints. Probably could be better named?
     */
    if (setScriptSentences.match(action)) {
        return action.payload;
    }
    return state;
}

export function docToScriptOptions(
    state: Immutable.Map<string, ScriptOption[]> = Immutable.Map(),
    action: AnyAction,
) {
    // data structure is a map of scriptId to ScriptOption[]
    if (setDocToScriptOptions.match(action)) {
        // update RawScriptOption to ScriptOption by adding a uuid for each script option
        // uuid = scriptId + index (so it's consistent when running multiple times)
        const scriptOptions = action.payload.scriptOptions.map((scriptOption, index) => {
            return {
                tone: '',
                ...scriptOption,
                uuid: `${action.payload.scriptId}-${index}`,
            };
        });
        return state.set(action.payload.scriptId, scriptOptions);
    }
    if (updateDocToScriptOption.match(action)) {
        const scriptOptionList = state.get(action.payload.scriptId);
        if (scriptOptionList) {
            const newScriptOptionList = scriptOptionList.map((scriptOption) => {
                if (scriptOption.uuid === action.payload.scriptOption.uuid) {
                    scriptOption = action.payload.scriptOption;
                }
                return scriptOption;
            });
            return state.set(action.payload.scriptId, newScriptOptionList);
        }
    }
    return state;
}

export function isFetchingScriptOptionAIScript(
    state: Immutable.Map<string, boolean> = Immutable.Map(),
    action: AnyAction,
) {
    if (setIsFetchingScriptOptionAIScript.match(action)) {
        return state.set(action.payload.scriptOptionId, action.payload.isLoading);
    }
    return state;
}

export function longestSupportedScriptDuration(state: number | null = null, action: AnyAction) {
    if (setLongestSupportedScriptDuration.match(action)) {
        return action.payload;
    }
    return state;
}

export function isFetchingAIScriptOptions(
    state: Immutable.Map<string, boolean> = Immutable.Map(),
    action: AnyAction,
) {
    if (setIsFetchingAIScriptOptions.match(action)) {
        if (state.has(action.payload.scriptId)) {
            state = state.set(action.payload.scriptId, action.payload.isLoading);
        } else {
            state = state.set(action.payload.scriptId, action.payload.isLoading);
        }
        return state;
    }
    return state;
}

export function isFetchingScriptFromIdea(state = false, action: Action) {
    if (setIsFetchingScriptFromIdea.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIAvatarVoicePreset(state: number | null = null, action: Action) {
    if (setCurrentAIAvatarVoicePreset.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIAvatar(state: number | null = null, action: Action) {
    if (setCurrentAIAvatar.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIVoice(state: string | null = null, action: Action) {
    if (setCurrentAIVoice.match(action)) {
        return action.payload;
    }
    return state;
}

export function ideaOutlineData(
    state: ImmutableIdeaOutlineValueSet = DEFAULT_IDEA_OUTLINE_VALUES,
    action: Action,
) {
    if (setIdeaOutlineSubject.match(action)) {
        return state.set('subject', action.payload);
    } else if (setIdeaOutlineReference.match(action)) {
        const references = state.get('references');
        const newReferences = references.update(
            action.payload.index,
            () => action.payload.reference,
        );
        return state.set('references', newReferences);
    } else if (addIdeaOutlineReference.match(action)) {
        const references = state.get('references');
        const newReferences = references.push('');
        return state.set('references', newReferences);
    } else if (deleteIdeaOutlineReference.match(action)) {
        const references = state.get('references');
        const newReferences = references.delete(action.payload);
        return state.set('references', newReferences);
    }
    return state;
}

export function isUploadingScriptImages(state = false, action: Action) {
    if (setIsUploadingScriptImages.match(action)) {
        return action.payload;
    }
    return state;
}

export function targetDuration(state: number | null = null, action: Action) {
    if (setTargetDuration.match(action)) {
        return action.payload;
    }
    return state;
}

export function groundhogAnalysis(state: GroundhogAnalysisResponse | null = null, action: Action) {
    if (setGroundhogAnalysis.match(action)) {
        return action.payload;
    }
    if (clearGroundhogAnalysisForText.match(action)) {
        if (state === null) {
            return null;
        }
        // filter out any sentences that match the provided text
        const filteredAnalysis = state.script_analysis.filter(
            (analysis) => analysis.sentence_text !== action.payload.text,
        );
        return {
            ...state,
            script_analysis: filteredAnalysis,
        };
    }
    return state;
}

export function selectedBlockText(state: string | null = null, action: AnyAction): string | null {
    if (setSelectedBlockText.match(action)) {
        return action.payload;
    }
    return state;
}

export function isConvertingToVoiceover(state: boolean = false, action: AnyAction): boolean {
    if (setIsConvertingToVoiceover.match(action)) {
        return action.payload;
    }
    return state;
}

export const script_composer = combineReducers({
    docToScriptPage,
    docToScriptData,
    docToScriptImportType,
    scriptCache,
    docToScriptOptions,
    isFetchingScriptOptionAIScript,
    isFetchingAIScriptOptions,
    isFetchingScriptFromIdea,
    isUploadingScriptImages,
    selectedScriptOption,
    longestSupportedScriptDuration,
    editedScript,
    editedScriptInitialWordCount,
    editedScriptLanguage,
    currentAIAvatarVoicePreset,
    currentAIAvatar,
    currentAIVoice,
    ideaOutlineData,
    scriptSentences,
    targetDuration,
    groundhogAnalysis,
    selectedBlockText,
    isConvertingToVoiceover,
});

export type ScriptComposerState = ReturnType<typeof script_composer>;
